import grotto from './assets/grotto.svg';
import grottoWebp from './assets/grotto.webp';
import { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react';
import Hls from 'hls.js';
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import logger from './logger.js';
import wsManager from './websocket.js';
import WebGLCanvas from './components/WebGLCanvas.jsx';
import WebGLBackground from './components/WebGLBackground.jsx';
import WebGLSparkles from './effects/WebGLSparkles.js';
import WebGLConfetti from './effects/WebGLConfetti.js';
import WebGLFireball from './effects/WebGLFireball.js';
import WebGLLightning from './effects/WebGLLightning.js';
import WebGLSmoke from './effects/WebGLSmoke.js';
import WebGLLasers from './effects/WebGLLasers.js';
import WebGLChicken from './effects/WebGLChicken.js';
import WebGLBird from './effects/WebGLBird.js';

const DEFAULT_ARCHIVE_PLAYLIST_ID = 'PLnD5HfA5-m75-9SYmifi34jfZK8sDkiQR';

// Audio-reactive ping pong ball
function AudioVisualizer({ videoRef, audioRef, isPlaying, activeEffects = [], onAudioData = null }) {
  const useWebGLEffects = true; // Set to true to use WebGL instead of DOM effects
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const animationRef = useRef(null);
  const ballsRef = useRef(new Map()); // Store multiple balls by effect ID
  const trailsRef = useRef(new Map()); // Store trails for each ball
  const sparklesRef = useRef(new Map()); // Store sparkles by effect ID
  const confettiRef = useRef(new Map()); // Store confetti by effect ID
  const lightningRef = useRef(new Map()); // Store lightning by effect ID
  const smokeRef = useRef(new Map()); // Store smoke by effect ID
  const initializedRef = useRef(false); // Track if audio is initialized

  // Initialize audio context only once
  useEffect(() => {
    const mediaElement = audioRef?.current || videoRef?.current;
    
    if (!initializedRef.current && mediaElement && isPlaying) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        sourceRef.current = audioContextRef.current.createMediaElementSource(mediaElement);
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;
        
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
        
        initializedRef.current = true;
        
      } catch (error) {
        console.log('Audio visualizer failed:', error);
      }
    }

    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, [isPlaying]); // Only depends on media elements and playing state

  // Separate audio analysis loop that runs independently of visual effects
  useEffect(() => {
    if (!onAudioData) return;

    let audioAnimationRef = null;
    
    const audioAnalysisLoop = () => {
      const analyser = analyserRef.current;
      const bufferLength = analyser ? analyser.frequencyBinCount : 0;
      const dataArray = analyser ? new Uint8Array(bufferLength) : null;

      // Audio analysis (independent of visual effects)
      let bassLevel = 0;
      let midLevel = 0, highLevel = 0, averageLevel = 0;
      let kickLevel = 0, bassPunchLevel = 0, clapLevel = 0, highLevelRaw = 0;
      
      if (isPlaying && initializedRef.current && analyser) {
        analyser.getByteFrequencyData(dataArray);
        
        // Calculate audio frequency bands with safety checks
        if (dataArray.length > 0) {
          // Focus on different frequency ranges for kicks and claps
          const kickData = dataArray.slice(0, Math.min(16, dataArray.length));    // Deep bass (kicks)
          const bassData = dataArray.slice(16, Math.min(48, dataArray.length));   // Low-mid (bass/kicks)
          const clapData = dataArray.slice(48, Math.min(96, dataArray.length));   // Mid (claps/snares)
          const highData = dataArray.slice(128, Math.min(256, dataArray.length)); // High (hi-hats/cymbals)
          
          // Calculate raw levels
          if (kickData.length > 0) {
            kickLevel = kickData.reduce((a, b) => a + b, 0) / kickData.length / 255;
          }
          if (bassData.length > 0) {
            bassPunchLevel = bassData.reduce((a, b) => a + b, 0) / bassData.length / 255;
          }
          if (clapData.length > 0) {
            clapLevel = clapData.reduce((a, b) => a + b, 0) / clapData.length / 255;
          }
          if (highData.length > 0) {
            highLevelRaw = highData.reduce((a, b) => a + b, 0) / highData.length / 255;
          }
          
          // Define thresholds - lower for kicks since they're often quieter than claps
          const kickThreshold = 0.15;   // Lower threshold for kicks (they're often deeper/quieter)
          const clapThreshold = 0.25;   // Higher threshold for claps (they're usually louder/sharper)  
          const highThreshold = 0.4;    // High threshold for hi-hats
          
          // Combine kick frequencies for better kick detection
          const combinedKickLevel = Math.max(kickLevel, bassPunchLevel * 0.8); // Use bass punch as backup kick detection
          
          // Apply thresholds with steep exponential boost 
          bassLevel = combinedKickLevel > kickThreshold ? Math.pow((combinedKickLevel - kickThreshold) / (1 - kickThreshold), 2.5) : 0;
          midLevel = clapLevel > clapThreshold ? Math.pow((clapLevel - clapThreshold) / (1 - clapThreshold), 3.0) : 0;
          highLevel = highLevelRaw > highThreshold ? Math.pow((highLevelRaw - highThreshold) / (1 - highThreshold), 4) : 0;
          
          // Only use bass frequencies - ignore mids and highs completely
          averageLevel = bassLevel;
        }
      }
      
      // Always pass audio data to callback for WebGL background (even if zeros when not playing)
      const audioDataToPass = {
        kickLevel: kickLevel || 0,
        bassPunchLevel: bassPunchLevel || 0,
        clapLevel: clapLevel || 0,
        highLevelRaw: highLevelRaw || 0,
        bassLevel: bassLevel || 0,
        midLevel: midLevel || 0,
        highLevel: highLevel || 0,
        averageLevel: averageLevel || 0
      };
      onAudioData(audioDataToPass);
      
      audioAnimationRef = requestAnimationFrame(audioAnalysisLoop);
    };

    // Start the audio analysis loop
    audioAnalysisLoop();

    return () => {
      if (audioAnimationRef) {
        cancelAnimationFrame(audioAnimationRef);
      }
    };
  }, [onAudioData, isPlaying]); // Run when audio callback changes or play state changes

  // Handle effects and animation
  useEffect(() => {    
    const fireballEffects = activeEffects.filter(effect => effect.type === 'fireball');
    const sparkleEffects = activeEffects.filter(effect => effect.type === 'sparkles');
    const confettiEffects = activeEffects.filter(effect => effect.type === 'confetti');
    const lightningEffects = activeEffects.filter(effect => effect.type === 'lightning');
    const smokeEffects = activeEffects.filter(effect => effect.type === 'smoke');
    // Check for effects that need immediate cleanup (exclude smoke since it fades out gradually)
    const hasImmediateEffects = fireballEffects.length > 0 || sparkleEffects.length > 0 || confettiEffects.length > 0 || lightningEffects.length > 0;
    // Check if we have any smoke particles still fading out
    const hasFadingSmoke = smokeRef.current.size > 0;
    const hasVisualEffects = hasImmediateEffects || smokeEffects.length > 0 || hasFadingSmoke;
    
    // Always run visual effects, regardless of audio state
    if (!hasVisualEffects) {
      // Remove body overflow hidden
      document.body.style.overflow = '';
      document.body.style.maxWidth = '';
      document.body.style.maxHeight = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.maxWidth = '';
      document.documentElement.style.maxHeight = '';
      
      // Clean up all balls, trails, sparkles, confetti, and lightning
      ballsRef.current.forEach((ball, id) => {
        if (ball.element) {
          document.body.removeChild(ball.element);
        }
      });
      ballsRef.current.clear();
      trailsRef.current.clear();
      
      if (!useWebGLEffects) {
        sparklesRef.current.forEach((sparkleArray, id) => {
          sparkleArray.forEach(sparkle => {
            if (sparkle.element) {
              document.body.removeChild(sparkle.element);
            }
          });
        });
        sparklesRef.current.clear();
      }
      
      confettiRef.current.forEach((confettiArray, id) => {
        confettiArray.forEach(piece => {
          if (piece.element) {
            document.body.removeChild(piece.element);
          }
        });
      });
      confettiRef.current.clear();
      
      lightningRef.current.forEach((lightningArray, id) => {
        lightningArray.forEach(bolt => {
          if (bolt.element) {
            document.body.removeChild(bolt.element);
          }
        });
      });
      lightningRef.current.clear();
      
      // Just fade out all smoke elements with CSS transition
      smokeRef.current.forEach((smokeArray, id) => {
        smokeArray.forEach(cloud => {
          if (cloud.element) {
            cloud.element.style.transition = 'opacity 8s ease-out';
            cloud.element.style.opacity = '0';
            // Remove after fade completes
            setTimeout(() => {
              if (cloud.element && cloud.element.parentNode) {
                document.body.removeChild(cloud.element);
              }
            }, 8000);
          }
        });
      });
      smokeRef.current.clear();
      
      
      // Clean up all effect elements
      document.querySelectorAll('[id^="trail-"], [id^="sparkle-"], [id^="confetti-"], [id^="lightning-"], [id^="rainbow-"]').forEach(element => {
        element.remove();
      });
      
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const initializeAudioVisualizer = () => {
      // Prevent scrollbars from effects extending beyond viewport
      document.body.style.cssText += '; overflow: hidden !important; max-width: 100vw !important; max-height: 100vh !important;';
      document.documentElement.style.cssText += '; overflow: hidden !important; max-width: 100vw !important; max-height: 100vh !important;';
      
      const analyser = analyserRef.current;
      const bufferLength = analyser ? analyser.frequencyBinCount : 0;
      const dataArray = analyser ? new Uint8Array(bufferLength) : null;

        // Animation loop with audio-reactive border effects
        const animate = () => {
          
          // Get current effects (fresh each frame)
          const currentFireballEffects = activeEffects.filter(effect => effect.type === 'fireball');
          const currentSparkleEffects = activeEffects.filter(effect => effect.type === 'sparkles');
          const currentConfettiEffects = activeEffects.filter(effect => effect.type === 'confetti');
          const currentLightningEffects = activeEffects.filter(effect => effect.type === 'lightning');
          const currentSmokeEffects = activeEffects.filter(effect => effect.type === 'smoke');
          const hasAnyEffects = currentFireballEffects.length > 0 || currentSparkleEffects.length > 0 || currentConfettiEffects.length > 0 || currentLightningEffects.length > 0 || currentSmokeEffects.length > 0;
          
          // Stop animation if no more effects AND no audio callback needed
          if (!hasAnyEffects && !onAudioData) {
            // Clean up all balls, trails, sparkles, confetti, and lightning
            ballsRef.current.forEach((ball, id) => {
              if (ball.element) {
                document.body.removeChild(ball.element);
              }
            });
            ballsRef.current.clear();
            trailsRef.current.clear();
            
            sparklesRef.current.forEach((sparkleArray, id) => {
              sparkleArray.forEach(sparkle => {
                if (sparkle.element) {
                  document.body.removeChild(sparkle.element);
                }
              });
            });
            sparklesRef.current.clear();
            
            confettiRef.current.forEach((confettiArray, id) => {
              confettiArray.forEach(piece => {
                if (piece.element) {
                  document.body.removeChild(piece.element);
                }
              });
            });
            confettiRef.current.clear();
            
            lightningRef.current.forEach((lightningArray, id) => {
              lightningArray.forEach(bolt => {
                if (bolt.element) {
                  document.body.removeChild(bolt.element);
                }
              });
            });
            lightningRef.current.clear();
      
      // Just fade out all smoke elements with CSS transition
      smokeRef.current.forEach((smokeArray, id) => {
        smokeArray.forEach(cloud => {
          if (cloud.element) {
            cloud.element.style.transition = 'opacity 8s ease-out';
            cloud.element.style.opacity = '0';
            // Remove after fade completes
            setTimeout(() => {
              if (cloud.element && cloud.element.parentNode) {
                document.body.removeChild(cloud.element);
              }
            }, 8000);
          }
        });
      });
      smokeRef.current.clear();
            
            
            // Clean up all effect elements
            document.querySelectorAll('[id^="trail-"], [id^="sparkle-"], [id^="confetti-"], [id^="lightning-"], [id^="rainbow-"]').forEach(element => {
              element.remove();
            });
            
            animationRef.current = null;
            return;
          }
          
          // Audio analysis is now handled in separate loop above
          
          // Handle fireball effects (skip if using WebGL effects)
          if (!useWebGLEffects) {
            // Update existing balls and create new ones for new effects
            currentFireballEffects.forEach((effect) => {
            // Create ball if it doesn't exist
            if (!ballsRef.current.has(effect.id)) {
              const ball = document.createElement('div');
              ball.id = `audio-ball-${effect.id}`;
              ball.style.cssText = `
                position: fixed;
                border-radius: 50%;
                pointer-events: none;
                z-index: 9999;
                transition: none;
                filter: drop-shadow(0 0 10px rgba(255, 100, 0, 0.8));
              `;
              document.body.appendChild(ball);
              
              // Random starting position and velocity for each ball
              const ballState = {
                x: Math.random() * (window.innerWidth - 100) + 50,
                y: Math.random() * (window.innerHeight - 100) + 50,
                vx: (Math.random() - 0.5) * 10 + 2, // Random velocity -3 to +7
                vy: (Math.random() - 0.5) * 10 + 2,
                size: 20
              };
              
              ballsRef.current.set(effect.id, { element: ball, state: ballState });
              trailsRef.current.set(effect.id, []);
            }
          });
          
          // Remove balls for effects that no longer exist
          const activeEffectIds = new Set(currentFireballEffects.map(e => e.id));
          ballsRef.current.forEach((ball, id) => {
            if (!activeEffectIds.has(id)) {
              if (ball.element) {
                document.body.removeChild(ball.element);
              }
              ballsRef.current.delete(id);
              trailsRef.current.delete(id);
              
              // Clean up trail elements for this specific ball
              document.querySelectorAll(`[id^="trail-${id}-"]`).forEach(element => {
                element.remove();
              });
            }
          });
          
          // Update each ball
          ballsRef.current.forEach((ballData, effectId) => {
            const ball = ballData.element;
            const state = ballData.state;
            const trail = trailsRef.current.get(effectId) || [];
            
            // Update ball size based on bass (20px to 60px)
            const ballSize = 20 + (bassLevel * 40);
            state.size = ballSize;
            
            // Add current position to trail
            trail.push({ 
              x: state.x, 
              y: state.y, 
              size: ballSize,
              time: Date.now()
            });
            
            // Keep only last 15 trail points
            if (trail.length > 15) {
              trail.shift();
            }
            trailsRef.current.set(effectId, trail);
            
            // Update position - ping pong physics
            state.x += state.vx;
            state.y += state.vy;
            
            // Bounce off walls
            if (state.x <= ballSize/2 || state.x >= window.innerWidth - ballSize/2) {
              state.vx = -state.vx;
            }
            if (state.y <= ballSize/2 || state.y >= window.innerHeight - ballSize/2) {
              state.vy = -state.vy;
            }
            
            // Keep in bounds
            state.x = Math.max(ballSize/2, Math.min(window.innerWidth - ballSize/2, state.x));
            state.y = Math.max(ballSize/2, Math.min(window.innerHeight - ballSize/2, state.y));
            
            // Update ball appearance
            ball.style.width = `${ballSize}px`;
            ball.style.height = `${ballSize}px`;
            ball.style.left = `${state.x - ballSize/2}px`;
            ball.style.top = `${state.y - ballSize/2}px`;
            ball.style.background = `linear-gradient(45deg, #ff6b35, #f7931e, #ffd23f)`;
            
            // Update fire trail for this ball
            const now = Date.now();
            trail.forEach((point, index) => {
              const age = now - point.time;
              const maxAge = 300; // Trail lasts 300ms
              
              if (age < maxAge) {
                const opacity = 1 - (age / maxAge);
                const trailSize = point.size * (0.3 + opacity * 0.7); // Shrink as it fades
                
                let trailElement = document.getElementById(`trail-${effectId}-${index}`);
                if (!trailElement) {
                  trailElement = document.createElement('div');
                  trailElement.id = `trail-${effectId}-${index}`;
                  trailElement.style.cssText = `
                    position: fixed;
                    border-radius: 50%;
                    pointer-events: none;
                    z-index: 9998;
                    transition: none;
                  `;
                  document.body.appendChild(trailElement);
                }
                
                // Flat fire trail with extreme fade-out
                const fireHue = 60 - (opacity * 60); // 60 (yellow) to 0 (red)
                const fadeOpacity = Math.pow(opacity, 3); // Much more extreme fade
                trailElement.style.width = `${trailSize}px`;
                trailElement.style.height = `${trailSize}px`;
                trailElement.style.left = `${point.x - trailSize/2}px`;
                trailElement.style.top = `${point.y - trailSize/2}px`;
                trailElement.style.background = `hsla(${fireHue}, 100%, ${70 - (fadeOpacity * 30)}%, ${fadeOpacity * 0.6})`;
                trailElement.style.filter = `blur(${(1 - opacity) * 3}px)`;
              }
            });
            
            // Clean up old trail elements for this ball
            document.querySelectorAll(`[id^="trail-${effectId}-"]`).forEach((element, index) => {
              if (index >= trail.length) {
                element.remove();
              }
            });
          });
          } // End of useWebGLEffects check for fireball
          
          // Handle sparkles effects (skip if using WebGL effects)
          if (!useWebGLEffects) {
            currentSparkleEffects.forEach((effect) => {
              // Create sparkles if they don't exist
              if (!sparklesRef.current.has(effect.id)) {
              const sparkles = [];
              const sparkleCount = 15; // Number of sparkles per effect
              
              for (let i = 0; i < sparkleCount; i++) {
                const sparkle = document.createElement('div');
                sparkle.id = `sparkle-${effect.id}-${i}`;
                sparkle.style.cssText = `
                  position: fixed;
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  pointer-events: none;
                  z-index: 9999;
                  transition: none;
                `;
                document.body.appendChild(sparkle);
                
                // Random starting position and movement
                const sparkleData = {
                  element: sparkle,
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                  vx: (Math.random() - 0.5) * 2, // Slower movement
                  vy: (Math.random() - 0.5) * 2,
                  life: 1.0, // Sparkle life (1 = full, 0 = dead)
                  decay: 0.002 + Math.random() * 0.003, // Random decay rate
                  baseOpacity: 0.8 + Math.random() * 0.2,
                  twinkleSpeed: 2 + Math.random() * 3,
                  hue: Math.random() * 60 + 180 // Blue to purple range
                };
                
                sparkles.push(sparkleData);
              }
              
              sparklesRef.current.set(effect.id, sparkles);
            }
          });
          
          if (!useWebGLEffects) {
            // Remove sparkles for effects that no longer exist
            const activeSparkleIds = new Set(currentSparkleEffects.map(e => e.id));
            sparklesRef.current.forEach((sparkleArray, id) => {
              if (!activeSparkleIds.has(id)) {
                sparkleArray.forEach(sparkle => {
                  if (sparkle.element) {
                    document.body.removeChild(sparkle.element);
                  }
                });
                sparklesRef.current.delete(id);
              }
            });
            
            // Update each sparkle
            sparklesRef.current.forEach((sparkleArray, effectId) => {
            sparkleArray.forEach((sparkle, index) => {
              // Update position with gentle floating movement
              sparkle.x += sparkle.vx;
              sparkle.y += sparkle.vy;
              
              // Add gentle wave motion
              sparkle.y += Math.sin(Date.now() * 0.001 + index) * 0.5;
              sparkle.x += Math.cos(Date.now() * 0.0008 + index) * 0.3;
              
              // Keep sparkles within screen bounds
              if (sparkle.x < 0) sparkle.x = 0;
              if (sparkle.x > window.innerWidth) sparkle.x = window.innerWidth;
              if (sparkle.y < 0) sparkle.y = 0;
              if (sparkle.y > window.innerHeight) sparkle.y = window.innerHeight;
              
              // Update life and create twinkling effect
              sparkle.life -= sparkle.decay;
              if (sparkle.life <= 0) {
                sparkle.life = 1.0; // Respawn
                sparkle.x = Math.random() * window.innerWidth;
                sparkle.y = Math.random() * window.innerHeight;
              }
              
              // Twinkling opacity
              const twinkle = Math.sin(Date.now() * 0.001 * sparkle.twinkleSpeed + index) * 0.3 + 0.7;
              const opacity = sparkle.baseOpacity * sparkle.life * twinkle;
              
              // Audio reactivity - sparkles get brighter with bass
              const audioBoost = 1 + (bassLevel * 2);
              const finalOpacity = Math.min(opacity * audioBoost, 1);
              
              // Update sparkle appearance
              const size = 6 + (bassLevel * 8) + Math.sin(Date.now() * 0.005 + index) * 2;
              sparkle.element.style.width = `${size}px`;
              sparkle.element.style.height = `${size}px`;
              sparkle.element.style.left = `${sparkle.x - size/2}px`;
              sparkle.element.style.top = `${sparkle.y - size/2}px`;
              sparkle.element.style.background = `hsla(${sparkle.hue}, 80%, 80%, ${finalOpacity})`;
              sparkle.element.style.boxShadow = `0 0 ${size * 2}px hsla(${sparkle.hue}, 80%, 80%, ${finalOpacity * 0.5})`;
            });
          });
          } // End of sparkles update section
          } // End of useWebGLEffects check
          
          // Handle confetti effects (skip if using WebGL effects)
          if (!useWebGLEffects) {
            currentConfettiEffects.forEach((effect) => {
            // Create confetti if it doesn't exist
            if (!confettiRef.current.has(effect.id)) {
              const confettiPieces = [];
              const confettiCount = 30; // Number of confetti pieces per effect
              
              for (let i = 0; i < confettiCount; i++) {
                const piece = document.createElement('div');
                piece.id = `confetti-${effect.id}-${i}`;
                piece.style.cssText = `
                  position: fixed;
                  width: 8px;
                  height: 12px;
                  pointer-events: none;
                  z-index: 9999;
                  transition: none;
                `;
                document.body.appendChild(piece);
                
                // Random confetti properties
                const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff'];
                const shapes = ['rectangle', 'square', 'circle'];
                const shape = shapes[Math.floor(Math.random() * shapes.length)];
                
                const pieceData = {
                  element: piece,
                  x: Math.random() * window.innerWidth,
                  y: -20, // Start above screen
                  vx: (Math.random() - 0.5) * 6, // Gentler horizontal spread
                  vy: Math.random() * 2 + 3, // Slower initial falling speed
                  rotation: Math.random() * 360,
                  rotationSpeed: (Math.random() - 0.5) * 8, // Slightly slower rotation
                  gravity: 0.25, // Lighter gravity for more realistic float
                  color: colors[Math.floor(Math.random() * colors.length)],
                  shape: shape,
                  life: 1.0,
                  decay: 0.008 + Math.random() * 0.004
                };
                
                // Set shape-specific styles
                if (shape === 'circle') {
                  piece.style.borderRadius = '50%';
                  piece.style.width = '6px';
                  piece.style.height = '6px';
                } else if (shape === 'square') {
                  piece.style.width = '8px';
                  piece.style.height = '8px';
                }
                
                piece.style.backgroundColor = pieceData.color;
                confettiPieces.push(pieceData);
              }
              
              confettiRef.current.set(effect.id, confettiPieces);
            }
          });
          
          // Remove confetti for effects that no longer exist
          const activeConfettiIds = new Set(currentConfettiEffects.map(e => e.id));
          confettiRef.current.forEach((confettiArray, id) => {
            if (!activeConfettiIds.has(id)) {
              confettiArray.forEach(piece => {
                if (piece.element) {
                  document.body.removeChild(piece.element);
                }
              });
              confettiRef.current.delete(id);
            }
          });
          
          // Update each confetti piece
          confettiRef.current.forEach((confettiArray, effectId) => {
            confettiArray.forEach((piece, index) => {
              // Apply gravity and movement
              piece.vy += piece.gravity;
              piece.x += piece.vx;
              piece.y += piece.vy;
              piece.rotation += piece.rotationSpeed;
              
              // Air resistance (stronger for confetti)
              piece.vx *= 0.995;
              piece.vy *= 0.997;
              
              // Settle on floor (no bouncing)
              if (piece.y > window.innerHeight - 10) {
                piece.y = window.innerHeight - 10;
                piece.vy = 0; // Stop vertical movement
                piece.vx *= 0.9; // Apply friction to horizontal movement
                piece.rotationSpeed *= 0.95; // Slow down rotation as it settles
              }
              
              // Respawn if off screen or faded
              piece.life -= piece.decay;
              if (piece.life <= 0 || piece.x < -50 || piece.x > window.innerWidth + 50) {
                piece.life = 1.0;
                piece.x = Math.random() * window.innerWidth;
                piece.y = -20;
                piece.vx = (Math.random() - 0.5) * 8;
                piece.vy = Math.random() * 3 + 5;
                piece.rotation = Math.random() * 360;
              }
              
              // Audio reactivity - confetti gets more chaotic with bass
              const audioJitter = bassLevel * 3;
              const jitterX = (Math.random() - 0.5) * audioJitter;
              const jitterY = (Math.random() - 0.5) * audioJitter;
              
              // Update confetti appearance
              const finalX = piece.x + jitterX;
              const finalY = piece.y + jitterY;
              const opacity = piece.life;
              
              piece.element.style.left = `${finalX}px`;
              piece.element.style.top = `${finalY}px`;
              piece.element.style.transform = `rotate(${piece.rotation}deg)`;
              piece.element.style.opacity = opacity;
              
              // Add some audio-reactive color intensity
              const colorIntensity = 1 + (bassLevel * 0.5);
              piece.element.style.filter = `brightness(${colorIntensity})`;
            });
          });
          } // End of useWebGLEffects check for confetti
          
          // Handle lightning effects (skip if using WebGL effects)
          if (!useWebGLEffects) {
          currentLightningEffects.forEach((effect) => {
            // Create lightning bolts if they don't exist
            if (!lightningRef.current.has(effect.id)) {
              const lightningBolts = [];
              const boltCount = 3; // Number of lightning bolts per effect
              
              for (let i = 0; i < boltCount; i++) {
                // Create SVG container for jagged lightning
                const svgContainer = document.createElement('div');
                svgContainer.id = `lightning-${effect.id}-${i}`;
                svgContainer.style.cssText = `
                  position: fixed;
                  pointer-events: none;
                  z-index: 9999;
                  filter: drop-shadow(0 0 8px #fff) drop-shadow(0 0 15px #a8e6ff);
                `;
                
                const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.style.width = '100%';
                svg.style.height = '100%';
                
                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path.style.fill = 'none';
                path.style.strokeLinecap = 'round';
                path.style.strokeLinejoin = 'round';
                
                svg.appendChild(path);
                svgContainer.appendChild(svg);
                document.body.appendChild(svgContainer);
                
                // Generate jagged lightning path
                const generateLightningPath = (startX, startY, endX, endY, segments = 8) => {
                  const points = [[startX, startY]];
                  const dx = endX - startX;
                  const dy = endY - startY;
                  
                  // Create intermediate points with random jagged offsets
                  for (let i = 1; i < segments; i++) {
                    const progress = i / segments;
                    const baseX = startX + dx * progress;
                    const baseY = startY + dy * progress;
                    
                    // Add random jagged offset - more chaotic in middle segments
                    const chaos = Math.sin(progress * Math.PI) * 30; // Peak chaos in middle
                    const offsetX = (Math.random() - 0.5) * chaos;
                    const offsetY = (Math.random() - 0.5) * (chaos * 0.3);
                    
                    points.push([baseX + offsetX, baseY + offsetY]);
                  }
                  
                  points.push([endX, endY]);
                  
                  // Convert to SVG path string
                  let pathData = `M ${points[0][0]} ${points[0][1]}`;
                  for (let i = 1; i < points.length; i++) {
                    pathData += ` L ${points[i][0]} ${points[i][1]}`;
                  }
                  
                  return pathData;
                };
                
                // Random lightning bolt properties
                const startX = Math.random() * window.innerWidth;
                const startY = Math.random() * (window.innerHeight * 0.2);
                const endX = startX + (Math.random() - 0.5) * 200; // End somewhat near start
                const endY = startY + 200 + Math.random() * 300; // Go downward
                const width = Math.max(window.innerWidth, Math.abs(endX - startX) + 100);
                const height = Math.max(window.innerHeight, endY - startY + 100);
                
                const boltData = {
                  element: svgContainer,
                  svg: svg,
                  path: path,
                  startX: startX,
                  startY: startY,
                  endX: endX,
                  endY: endY,
                  width: width,
                  height: height,
                  opacity: 0,
                  life: 1.0,
                  decay: 0.02 + Math.random() * 0.03,
                  flashPhase: Math.random() * Math.PI * 2,
                  intensity: 0.5 + Math.random() * 0.5,
                  flickerSpeed: 15 + Math.random() * 10,
                  strokeWidth: 1 + Math.random() * 3,
                  generatePath: generateLightningPath
                };
                
                // Set initial SVG size and position
                svgContainer.style.width = `${width}px`;
                svgContainer.style.height = `${height}px`;
                svgContainer.style.left = `${Math.min(startX, endX) - 50}px`;
                svgContainer.style.top = `${startY}px`;
                
                svg.setAttribute('width', width);
                svg.setAttribute('height', height);
                svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
                
                // Generate initial jagged path
                const pathData = boltData.generatePath(
                  startX - Math.min(startX, endX) + 50,
                  0,
                  endX - Math.min(startX, endX) + 50,
                  endY - startY
                );
                path.setAttribute('d', pathData);
                path.setAttribute('stroke', '#ffffff');
                path.setAttribute('stroke-width', boltData.strokeWidth);
                
                lightningBolts.push(boltData);
              }
              
              lightningRef.current.set(effect.id, lightningBolts);
            }
          });
          
          // Remove lightning for effects that no longer exist
          const activeLightningIds = new Set(currentLightningEffects.map(e => e.id));
          lightningRef.current.forEach((boltArray, id) => {
            if (!activeLightningIds.has(id)) {
              boltArray.forEach(bolt => {
                if (bolt.element) {
                  document.body.removeChild(bolt.element);
                }
              });
              lightningRef.current.delete(id);
            }
          });
          
          // Update each lightning bolt
          lightningRef.current.forEach((boltArray, effectId) => {
            boltArray.forEach((bolt, index) => {
              // Update life and create flashing effect
              bolt.life -= bolt.decay;
              if (bolt.life <= 0) {
                bolt.life = 1.0; // Respawn for continuous lightning
                bolt.x = Math.random() * window.innerWidth;
                bolt.y = Math.random() * (window.innerHeight * 0.3);
                bolt.rotation = (Math.random() - 0.5) * 30;
                bolt.height = 100 + Math.random() * 200;
                bolt.width = 2 + Math.random() * 4;
                bolt.flashPhase = Math.random() * Math.PI * 2;
                
                // Update position and size
                bolt.element.style.left = `${bolt.x - bolt.width/2}px`;
                bolt.element.style.top = `${bolt.y}px`;
                bolt.element.style.transform = `rotate(${bolt.rotation}deg)`;
                bolt.element.style.width = `${bolt.width}px`;
                bolt.element.style.height = `${bolt.height}px`;
              }
              
              // Lightning flicker effect
              const time = Date.now() * 0.001;
              const flicker = Math.sin(time * bolt.flickerSpeed + bolt.flashPhase) * 0.3 + 0.7;
              const pulse = Math.sin(time * 8 + index) * 0.2 + 0.8;
              
              // Audio reactivity - lightning gets more intense with bass
              const audioIntensity = 1 + (bassLevel * 3);
              const baseOpacity = bolt.intensity * bolt.life * flicker * pulse;
              const finalOpacity = Math.min(baseOpacity * audioIntensity, 1);
              
              // Update lightning appearance
              bolt.element.style.opacity = finalOpacity;
              
              // Add screen flash effect for intense lightning
              if (finalOpacity > 0.8 && bassLevel > 0.3) {
                document.body.style.boxShadow = `inset 0 0 100px rgba(173, 216, 230, ${(finalOpacity - 0.8) * 0.3})`;
                setTimeout(() => {
                  document.body.style.boxShadow = '';
                }, 50);
              }
            });
          });
          } // End of useWebGLEffects check for lightning
          
          // Handle smoke effects (skip if using WebGL effects)
          if (!useWebGLEffects) {
          currentSmokeEffects.forEach((effect) => {
            // Create smoke clouds if they don't exist
            if (!smokeRef.current.has(effect.id)) {
              const smokeClouds = [];
              const cloudCount = 150; // Number of smoke clouds per effect
              
              for (let i = 0; i < cloudCount; i++) {
                const cloud = document.createElement('div');
                cloud.id = `smoke-${effect.id}-${i}`;
                cloud.style.cssText = `
                  position: fixed;
                  width: 60px;
                  height: 60px;
                  border-radius: 60% 40% 50% 70%;
                  pointer-events: none;
                  z-index: 9997;
                  background: radial-gradient(ellipse 120% 80% at 40% 60%, rgba(200, 200, 200, 0.7) 0%, rgba(160, 160, 160, 0.5) 40%, rgba(120, 120, 120, 0.3) 70%, transparent 100%);
                  filter: blur(12px);
                  transition: none;
                `;
                document.body.appendChild(cloud);
                
                // Random starting position - spread across multiple layers
                const smokeData = {
                  element: cloud,
                  x: Math.random() * window.innerWidth,
                  y: window.innerHeight + 20 - Math.random() * 200, // Start at different heights
                  vx: (Math.random() - 0.5) * 0.1, // Ultra slow horizontal drift
                  vy: -0.2 - Math.random() * 0.1, // Slow upward movement
                  life: 0.0, // Start invisible
                  maxLife: 1.0,
                  fadeInDuration: 3000 + Math.random() * 2000, // 3-5 seconds to fade in
                  fadeOutDuration: 6000 + Math.random() * 3000, // 6-9 seconds to fade out
                  decay: 0.0005 + Math.random() * 0.0005,
                  age: 0, // Track total age
                  totalLifespan: 25000 + Math.random() * 15000, // 25-40 seconds total
                  size: 60 + Math.random() * 40,
                  rotation: Math.random() * 360,
                  rotationSpeed: (Math.random() - 0.5) * 0.5
                };
                
                smokeClouds.push(smokeData);
              }
              
              smokeRef.current.set(effect.id, smokeClouds);
            }
          });
          
          // Handle smoke cleanup for effects that no longer exist
          const activeSmokeIds = new Set(currentSmokeEffects.map(e => e.id));
          smokeRef.current.forEach((smokeArray, id) => {
            if (!activeSmokeIds.has(id)) {
              // Mark particles for fade out instead of immediate removal
              smokeArray.forEach(cloud => {
                if (!cloud.effectEnded) {
                  cloud.effectEnded = true;
                  cloud.endFadeStart = Date.now();
                  cloud.endFadeStartOpacity = cloud.life; // Remember current opacity
                }
              });
            }
          });
          
          // Update smoke clouds
          smokeRef.current.forEach((smokeArray, effectId) => {
            smokeArray.forEach((cloud, index) => {
              // Update position with upward movement and drift
              cloud.x += cloud.vx;
              cloud.y += cloud.vy;
              cloud.rotation += cloud.rotationSpeed;
              
              // Add some wave-like movement
              cloud.x += Math.sin(Date.now() * 0.0002 + index) * 0.1;
              
              // Update age and handle fade in/out
              cloud.age += 16; // Assume 60fps, so ~16ms per frame
              
              let targetOpacity = 0;
              
              // If effect ended, fade out gradually with easing
              if (cloud.effectEnded) {
                const elapsed = Date.now() - cloud.endFadeStart;
                const fadeOutTime = 8000; // 8 seconds to fade out (longer for smoother fade)
                if (elapsed < fadeOutTime) {
                  const progress = elapsed / fadeOutTime;
                  // Use cubic easing for smoother, more natural fade
                  const easedProgress = progress * progress * progress;
                  targetOpacity = cloud.endFadeStartOpacity * (1 - easedProgress);
                } else {
                  // Completely faded - remove
                  if (cloud.element && cloud.element.parentNode) {
                    document.body.removeChild(cloud.element);
                  }
                  cloud.readyForRemoval = true;
                  targetOpacity = 0;
                }
              } else if (cloud.age < cloud.fadeInDuration) {
                // Fade in phase - use easing for smoother fade
                const progress = cloud.age / cloud.fadeInDuration;
                targetOpacity = progress * progress; // Quadratic easing for smoother start
              } else if (cloud.age < cloud.totalLifespan - cloud.fadeOutDuration) {
                // Full opacity phase
                targetOpacity = 1.0;
              } else if (cloud.age < cloud.totalLifespan) {
                // Fade out phase - use cubic easing for smoothest fade
                const fadeOutProgress = (cloud.totalLifespan - cloud.age) / cloud.fadeOutDuration;
                targetOpacity = fadeOutProgress * fadeOutProgress * fadeOutProgress; // Cubic easing for smoothest end
              } else {
                // Dead - only respawn if effect is still active
                const activeSmokeIds = new Set(currentSmokeEffects.map(e => e.id));
                if (activeSmokeIds.has(effectId)) {
                  // Effect still active - respawn
                  cloud.x = Math.random() * window.innerWidth;
                  cloud.y = window.innerHeight + 20 - Math.random() * 200;
                  cloud.age = 0;
                  cloud.size = 60 + Math.random() * 40;
                  cloud.fadeInDuration = 3000 + Math.random() * 2000;
                  cloud.fadeOutDuration = 6000 + Math.random() * 3000;
                  cloud.totalLifespan = 25000 + Math.random() * 15000;
                  targetOpacity = 0;
                } else {
                  // Effect ended - start final fade
                  if (!cloud.effectEnded) {
                    cloud.effectEnded = true;
                    cloud.endFadeStart = Date.now();
                    cloud.endFadeStartOpacity = cloud.life;
                  }
                  // Continue with effect ended logic above
                  const elapsed = Date.now() - cloud.endFadeStart;
                  const fadeOutTime = 8000; // Match the improved fade-out time
                  if (elapsed < fadeOutTime) {
                    const progress = elapsed / fadeOutTime;
                    // Use cubic easing for consistent, smooth fade
                    const easedProgress = progress * progress * progress;
                    targetOpacity = cloud.endFadeStartOpacity * (1 - easedProgress);
                  } else {
                    cloud.readyForRemoval = true;
                    targetOpacity = 0;
                  }
                }
              }
              
              cloud.life = targetOpacity;
              
              // Respawn if goes off screen
              if (cloud.y < -100) {
                cloud.x = Math.random() * window.innerWidth;
                cloud.y = window.innerHeight + 20 - Math.random() * 200;
                cloud.age = 0;
                cloud.life = 0;
              }
              
              // Keep within horizontal bounds
              if (cloud.x < -50) cloud.x = window.innerWidth + 50;
              if (cloud.x > window.innerWidth + 50) cloud.x = -50;
              
              // Audio reactive size
              const audioScale = 1 + bassLevel * 0.3;
              const finalSize = cloud.size * audioScale;
              
              // Update cloud appearance
              cloud.element.style.left = `${cloud.x - finalSize/2}px`;
              cloud.element.style.top = `${cloud.y - finalSize/2}px`;
              cloud.element.style.width = `${finalSize}px`;
              cloud.element.style.height = `${finalSize}px`;
              cloud.element.style.opacity = cloud.life * 0.6;
              cloud.element.style.transform = `rotate(${cloud.rotation}deg) scaleX(${1 + Math.sin(Date.now() * 0.001 + index) * 0.2}) scaleY(${1 + Math.cos(Date.now() * 0.0008 + index) * 0.15})`;
            });
            
            // Remove particles that are ready for removal
            smokeRef.current.set(effectId, smokeArray.filter(cloud => !cloud.readyForRemoval));
          });
          
          // Clean up empty effect arrays
          smokeRef.current.forEach((smokeArray, id) => {
            if (smokeArray.length === 0) {
              smokeRef.current.delete(id);
            }
          });
          } // End of useWebGLEffects check for smoke
          
          animationRef.current = requestAnimationFrame(animate);
        };
        
        // Start animation loop if we have effects OR if we need audio data for WebGL background
        if (activeEffects.length > 0 || onAudioData) {
          animate();
        }
    };

    initializeAudioVisualizer();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [activeEffects, onAudioData]); // Visual effects work independently of audio state, but restart when audio callback changes

  return null; // No visual element needed
}

function getRandomColor() {
  return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
}

function validateUsername(username) {
  // Only allow lowercase letters, numbers, underscores, and dashes
  const validPattern = /^[a-z0-9_-]+$/;
  return validPattern.test(username) && username.length >= 2 && username.length <= 20;
}

function sanitizeUsername(username) {
  // Convert to lowercase and remove invalid characters
  return username.toLowerCase().replace(/[^a-z0-9_-]/g, '');
}

function Marquee() {
  const marqueeItems = [
    "Chicken content on <a href='https://www.instagram.com/dutchdubs/' target='_blank'>Instagram</a>",
    "Dubs on <a href='https://dubbeldutch.bandcamp.com/' target='_blank'>BandCamp</a>",
    "Avoiding <a href='https://x.com/dubbeldutch' target='_blank'>twitter</a>",
    "Reading the news on <a href='https://bsky.app/profile/dubbeldutch.com' target='_blank'>BlueSky</a>",
    "Webmaster at <a href='https://dubbeldutch.com' target='_blank'>dubbeldutch.com</a>",
    "Sign up for the <a href='https://t.co/VOwuPQclP4' target='_blank'>mailing list</a>",
    "TL;DRs on <a href='https://dutchdubs.substack.com/' target='_blank'>Substack</a>",
  ];
  const marqueeText = marqueeItems.join(" • ") + " • ";

  // Triple the text to ensure smooth looping
  const repeatedText = marqueeText + marqueeText + marqueeText;

  return (
    <div className="marquee-container">
      <div className="marquee-content" dangerouslySetInnerHTML={{ __html: repeatedText }} />
    </div>
  );
}

function Chat({ checkStreamingStatus, isCollapsed, activeEffects, setActiveEffects, isChaosMode, userCount, setUserCount, userList, setUserList }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isClaimingNickname, setIsClaimingNickname] = useState(false);
  const [claimEmail, setClaimEmail] = useState('');
  const [claimNickname, setClaimNickname] = useState('');
  const [claimColor, setClaimColor] = useState(getRandomColor());
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingVerification, setPendingVerification] = useState(null);
  const [isVerifiedUser, setIsVerifiedUser] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [nickname, setNickname] = useState(localStorage.getItem('nickname') || '');
  const [color, setColor] = useState(localStorage.getItem('color') || getRandomColor());
  const [isNicknameSet, setIsNicknameSet] = useState(true); // Everyone starts with anonymous nickname
  const [serverAssignedNickname, setServerAssignedNickname] = useState(''); // Track server-assigned anonymous name
  const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);
  const [emojiVisibleMessageId, setEmojiVisibleMessageId] = useState(null);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [messageCount, setMessageCount] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [userJustSentMessage, setUserJustSentMessage] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [isAtTop, setIsAtTop] = useState(false);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const socketRef = useRef(null);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  
  // Refs for current values to avoid stale closures
  const isNicknameSetRef = useRef(isNicknameSet);
  const nicknameRef = useRef(nickname);
  
  // Update refs when state changes
  useEffect(() => {
    isNicknameSetRef.current = isNicknameSet;
  }, [isNicknameSet]);
  
  useEffect(() => {
    nicknameRef.current = nickname;
  }, [nickname]);

  // Auto-resize textarea function
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate the new height - use 20px as minimum to match CSS better
      const minHeight = 20;
      const maxHeight = 120;
      
      // Calculate content height
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(minHeight, scrollHeight), maxHeight);
      
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  // Adjust height when message changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [message, adjustTextareaHeight]);

  // Set initial height when component mounts
  useEffect(() => {
    adjustTextareaHeight();
  }, [adjustTextareaHeight]);

  // Adjust textarea height when user joins chat (isNicknameSet changes to true)
  // useLayoutEffect runs synchronously after DOM mutations but before paint
  useLayoutEffect(() => {
    if (isNicknameSet) {
      adjustTextareaHeight();
    }
  }, [isNicknameSet, adjustTextareaHeight]);

  // Close emoji pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on backdrop or outside reaction emoji picker
      if (emojiVisibleMessageId) {
        const picker = document.querySelector('[data-emoji-picker="reaction"]');
        const backdrop = event.target.classList.contains('emoji-picker-backdrop');
        if (backdrop || (picker && !picker.contains(event.target))) {
          setEmojiVisibleMessageId(null);
        }
      }
      
      // Check if click is on backdrop or outside text input emoji picker
      if (isEmojiPickerVisible) {
        const picker = document.querySelector('[data-emoji-picker="input"]');
        const emojiButton = document.querySelector('[data-emoji-button="input"]');
        const backdrop = event.target.classList.contains('emoji-picker-backdrop');
        if (backdrop || (picker && !picker.contains(event.target) && 
            emojiButton && !emojiButton.contains(event.target))) {
          setIsEmojiPickerVisible(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [emojiVisibleMessageId, isEmojiPickerVisible]);

  // Position tooltips dynamically
  const handleTooltipPosition = useCallback((event, tooltip) => {
    if (!tooltip) return;
    
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Default position: above the reaction
    let left = rect.left + rect.width / 2;
    let top = rect.top - 10; // 10px above
    
    // Adjust horizontal position if tooltip would go off-screen
    if (left - tooltipRect.width / 2 < 10) {
      left = 10 + tooltipRect.width / 2;
    } else if (left + tooltipRect.width / 2 > viewportWidth - 10) {
      left = viewportWidth - 10 - tooltipRect.width / 2;
    }
    
    // Adjust vertical position if tooltip would go off-screen
    if (top - tooltipRect.height < 10) {
      top = rect.bottom + 10; // Show below instead
    }
    
    tooltip.style.left = `${left - tooltipRect.width / 2}px`;
    tooltip.style.top = `${top - tooltipRect.height}px`;
  }, []);

  // Handle color update for verified users
  const handleColorUpdate = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('Not authenticated');
        return;
      }

      // For now, just update locally and notify via WebSocket
      // TODO: Create proper API endpoint for profile updates
      localStorage.setItem('color', color);
      
      // Notify server via WebSocket about color change
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const messageData = JSON.stringify({
          eventName: 'color_update',
          authToken: authToken,
          color: color
        });
        socketRef.current.send(messageData);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Color update error:', error);
    }
  };

  // Handle nickname claiming with email verification
  const handleClaimNickname = async () => {
    if (!claimEmail || !claimNickname) {
      console.error('Please enter both email and desired nickname');
      return;
    }

    try {
      const response = await fetch('/api/claim-username', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: claimEmail,
          handle: claimNickname,
          color: claimColor
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Store pending verification data
        setPendingVerification({
          email: claimEmail,
          handle: claimNickname,
          color: claimColor,
          debugCode: result.debugCode // For debugging
        });
        setShowVerificationForm(true);
        setIsClaimingNickname(false);
        console.log('Debug code:', result.debugCode); // For debugging
      } else {
        console.error(result.error || 'Failed to initiate username claim');
      }
    } catch (error) {
      console.error('Error claiming username:', error);
    }
  };

  // Handle login for existing users
  const handleLogin = async () => {
    if (!loginEmail) {
      console.error('Please enter your email');
      return;
    }
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginEmail
        }),
      });
      const result = await response.json();
      if (response.ok) {
        // Store pending verification data for login
        setPendingVerification({
          email: loginEmail,
          isLogin: true,
          debugCode: result.debugCode // For debugging
        });
        setShowVerificationForm(true);
        setIsLoggingIn(false);
        console.log('Login debug code:', result.debugCode); // For debugging
      } else {
        console.error(result.error || 'Failed to initiate login');
      }
    } catch (error) {
      console.error('Error logging in:', error);
    }
  };

  // Handle direct nickname update for verified users
  const handleNicknameUpdate = async () => {
    if (!nickname.trim()) {
      console.error('Please enter a nickname');
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch('/api/verify-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: 'verified_user@placeholder.com', // Placeholder for verified user updates
          code: 'VERIFIED_UPDATE',
          handle: nickname.trim(),
          color: color
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Update localStorage
        localStorage.setItem('nickname', result.user.handle);
        localStorage.setItem('color', result.user.color);
        
        // Notify server via WebSocket
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          const messageData = JSON.stringify({
            eventName: 'nickname_claimed',
            authToken: authToken,
            newNickname: result.user.handle
          });
          socketRef.current.send(messageData);
        }
        
        setIsEditing(false);
      } else {
        console.error(result.error || 'Failed to update nickname');
      }
    } catch (error) {
      console.error('Error updating nickname:', error);
    }
  };

  // Handle verification code submission
  const handleVerifyCode = async () => {
    if (!verificationCode || !pendingVerification) {
      console.error('Please enter the verification code');
      return;
    }

    try {
      // For login, we need to use the login verification endpoint
      // For registration, we use the existing verify-code endpoint
      const isLogin = pendingVerification.isLogin;
      const requestBody = isLogin ? 
        {
          email: pendingVerification.email,
          code: verificationCode
        } : 
        {
          email: pendingVerification.email,
          code: verificationCode,
          handle: pendingVerification.handle,
          color: pendingVerification.color
        };

      const endpoint = isLogin ? '/api/verify-login' : '/api/verify-code';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (response.ok) {
        // Store auth token
        localStorage.setItem('authToken', result.authToken);
        localStorage.setItem('nickname', result.user.handle);
        localStorage.setItem('color', result.user.color);
        
        // Update local state
        setNickname(result.user.handle);
        setColor(result.user.color);
        setIsVerifiedUser(true);
        
        // Notify server via WebSocket
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          const messageData = JSON.stringify({
            eventName: 'nickname_claimed',
            authToken: result.authToken,
            newNickname: result.user.handle
          });
          socketRef.current.send(messageData);
        }
        
        // Reset forms and close all modals
        setShowVerificationForm(false);
        setPendingVerification(null);
        setVerificationCode('');
        setClaimEmail('');
        setClaimNickname('');
        setLoginEmail('');
        setIsEditing(false); // Close the main edit profile modal
      } else {
        console.error(result.error || 'Verification failed');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
    }
  };

  const handleReact = (emoji, id) => {    
    if (!isNicknameSet) {
      console.log('[reaction] Cannot react: user has not joined chat');
      return;
    }
    
    // Find the message to check if it's a system message
    const messageId = emojiVisibleMessageId || id;    
    const user = nickname; // Using the nickname as the user identifier
    const reactionData = {
      eventName: 'reaction',
      messageId,
      emoji,
      user,
    };
  
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(reactionData));
    } else {
      console.warn('[reaction] WebSocket not open, cannot send reaction. State:', socketRef.current?.readyState);
    }
    setEmojiVisibleMessageId(null);
  };

  const handleEmojiSelect = (emoji) => {
    setMessage((prevMessage) => prevMessage + emoji.native);
    toggleEmojiPicker(null);
  };

  const toggleEmojiPicker = (messageId = null) => {
    if (!isNicknameSet && messageId) {
      return;
    }
    
    if (messageId) {
      setEmojiVisibleMessageId((prevId) => {
        return prevId === messageId ? null : messageId;
      });
    } else {
      setIsEmojiPickerVisible((prev) => {
        return !prev;
      });
    }
  };

  const loadOlderMessages = useCallback(async () => {    
    if (isLoadingOlderMessages || messages.length === 0) return;

    // Find the message with the smallest ID (oldest)
    // Since we're using column-reverse, the oldest message is at the end of the array
    const oldestMessage = messages[messages.length - 1];
        
    if (!oldestMessage || !oldestMessage.id) return;

    setIsLoadingOlderMessages(true);
    
    try {
      const isProduction = import.meta.env.MODE === 'production';
      const baseUrl = isProduction ? 'https://grotto.fm' : 'http://localhost:4000';
      const url = `${baseUrl}/api/messages/older?before=${oldestMessage.id}&limit=50&chaosMode=${isChaosMode}`;
      
      const response = await fetch(url);
      
      if (response.ok) {
        const olderMessages = await response.json();
        
        // Check if we received fewer messages than the limit (indicating we've reached the end)
        if (olderMessages.length < 50) {
          setHasMoreMessages(false);
        }
        
        setMessages(prevMessages => {          
          const newMessages = [...prevMessages, ...olderMessages];
          
          // Remove duplicates based on message ID
          const uniqueMessages = newMessages.filter((message, index, self) => 
            index === self.findIndex(m => m.id === message.id)
          );
          
          // Update message count to reflect total messages loaded
          setMessageCount(uniqueMessages.length);
          
          return uniqueMessages;
        });
        

      } else {
        console.error('[pagination] Failed to load older messages:', response.status);
      }
    } catch (error) {
      console.error('[pagination] Error loading older messages:', error);
    } finally {
      setIsLoadingOlderMessages(false);
    }
  }, [messages, isLoadingOlderMessages, isChaosMode]);

  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottomNow = scrollTop + clientHeight >= scrollHeight - 10;
    
    // With column-reverse, detect if user has reached the actual end of the message list
    // When scrollTop reaches its minimum possible value, user is at the true top (oldest messages)
    const minScrollTop = Math.min(0, clientHeight - scrollHeight);
    const isAtTopNow = scrollTop <= minScrollTop + 10; // User is at or very close to the true end of messages
    
    setIsAtBottom(isAtBottomNow);
    setIsAtTop(isAtTopNow);
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      // With column-reverse, scroll to top to show newest messages
      messagesContainerRef.current.scrollTop = 0;
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    logger.info('WEBSOCKET', 'connectWebSocket() called');
    
    // Using global WebSocket manager - no need to close existing connections

    // Use secure WebSocket for production, insecure for development
    const isProduction = import.meta.env.MODE === 'production';
    const wsUrl = isProduction 
      ? 'wss://grotto.fm' 
      : 'ws://localhost:4000';
    const ws = new WebSocket(wsUrl);
    
    // Store the WebSocket reference
    socketRef.current = ws;

    ws.onopen = () => {
      logger.info('WEBSOCKET', 'WebSocket connected successfully');
      reconnectAttempts.current = 0;
      
      // Check if user has auth token and authenticate
      const authToken = localStorage.getItem('authToken');
      console.log('Checking auth token on connection:', authToken ? 'found' : 'not found');
      if (authToken) {
        const authMessage = {
          eventName: 'authenticate',
          authToken: authToken
        };
        ws.send(JSON.stringify(authMessage));
        console.log('Sent authentication message with token:', authToken);
      } else {
        console.log('No auth token found, will receive anonymous nickname');
      }
      
      // Load initial messages via HTTP - inline to avoid dependency issues
      (async () => {
        try {
          const isProduction = import.meta.env.MODE === 'production';
          const baseUrl = isProduction ? 'https://grotto.fm' : 'http://localhost:4000';
          const response = await fetch(`${baseUrl}/api/messages/initial?limit=50&chaosMode=false`);
          
          if (response.ok) {
            const initialMessages = await response.json();

            setMessages(initialMessages);
            setMessageCount(initialMessages.length);
            
            // Since the API now handles filtering, we can trust the length
            if (initialMessages.length < 50) {
              setHasMoreMessages(false);
            }
          } else {
            console.error('Failed to load initial messages:', response.status);
          }

          // Load initial user list
          try {
            const userResponse = await fetch(`${baseUrl}/api/users`);
            if (userResponse.ok) {
              const userData = await userResponse.json();
              setUserCount(userData.userCount);
              setUserList(userData.users || []);
            }
          } catch (error) {
            console.error('Failed to load user list:', error);
          }
        } catch (error) {
          console.error('Error loading initial messages:', error);
        }
      })();
      
      // Send join announcement for returning users
      if (isNicknameSetRef.current && nicknameRef.current) {
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            const joinMessageData = {
              eventName: 'message',
              text: `${nicknameRef.current} has joined the chat`,
              nickname: '[system]',
              color: localStorage.getItem('color') || '#999999',
              id: Date.now(),
              isSystemMessage: true
            };
            ws.send(JSON.stringify(joinMessageData));
          } else {
            console.log('WebSocket not ready, state:', ws.readyState);
          }
        }, 500);
      } else {
        console.log('No stored user data, skipping join announcement');
      }
    };

    ws.onmessage = (event) => {
      try {
        const eventData = JSON.parse(event.data);
        console.log('[DEBUG] WebSocket message received:', eventData);

        // Handle auth_success event (single event, not array)
        if (eventData.eventName === 'auth_success') {
          console.log('[DEBUG] Received auth_success event, processing...');
          console.log(`Authentication successful: ${eventData.user.handle}`);
          console.log('Setting isVerifiedUser to true');
          setNickname(eventData.user.handle);
          setColor(eventData.user.color);
          setIsVerifiedUser(true);
          // Update localStorage
          localStorage.setItem('nickname', eventData.user.handle);
          localStorage.setItem('color', eventData.user.color);
          return; // Exit early since this is handled
        }

        // Handle single reaction update event
        if (eventData.eventName === 'reaction_update') {
          
          setMessages((prevMessages) => { 
            const updatedMessages = prevMessages.map((msg) => {
              if (msg.id === eventData.messageId) {
                return { 
                  ...msg, 
                  reactions: { ...eventData.reactions } 
                };
              }
              return msg;
            });
            
            return updatedMessages;
          });
          return;
        }

        // Handle user list update event
        if (eventData.eventName === 'user_list_update') {
          console.log('[USER_LIST] About to process user_list_update');
          console.log('[USER_LIST] Received user list update:', eventData);
          console.log('[USER_LIST] Current userList before update:', userList);
          setUserCount(eventData.userCount);
          setUserList(eventData.users || []);
          console.log('[USER_LIST] Set new userList to:', eventData.users || []);
          return;
        }

        // Handle nickname_assigned event
        if (eventData.eventName === 'nickname_assigned') {
          console.log(`Server assigned nickname: ${eventData.assignedNickname}`);
          setServerAssignedNickname(eventData.assignedNickname);
          setNickname(eventData.assignedNickname);
          setIsVerifiedUser(false);
          return;
        }

        // Handle effect events
        if (eventData.type === 'effect') {
          const effect = eventData.effect;
          
          // Add effect to state
          setActiveEffects(prev => [...prev, effect]);
          
          // Auto-remove effect after duration
          setTimeout(() => {
            setActiveEffects(prev => prev.filter(e => e.id !== effect.id));
          }, effect.duration);
          
          return;
        }
        if (Array.isArray(eventData)) {
          eventData.forEach((ev) => {

            if (ev.eventName === 'message') {
              // Handle new messages
              setMessages((prevMessages) => {
                // Check if the message already exists
                const messageExists = prevMessages.some((msg) => msg.id === ev.id);
                if (messageExists) {
                  // If message exists, update it (shouldn't happen with new server logic)
                  return prevMessages.map((msg) => {
                    if (msg.id === ev.id) {
                      return { ...msg, reactions: { ...ev.reactions } };
                    }
                    return msg;
                  });
                } else {
                  // If message doesn't exist, add it to the beginning (newest messages first)
                  const newMessages = [ev, ...prevMessages];
                  // Track that a new message was added for auto-scroll
                  setMessageCount(prev => prev + 1);
                  return newMessages;
                }
              });
            } else if (ev.eventName === 'reaction_update') {
              // Handle reaction updates from arrays (shouldn't happen with new server logic)
              setMessages((prevMessages) => {
                return prevMessages.map((msg) => {
                  if (msg.id === ev.messageId) {
                    return { 
                      ...msg, 
                      reactions: { ...ev.reactions } 
                    };
                  }
                  return msg;
                });
              });
            } else if (ev.eventName === 'stream_start') {
              checkStreamingStatus();
            } else if (ev.eventName === 'user_list_update') {
              console.log('[USER_LIST] About to process user_list_update');
              console.log('[USER_LIST] Received user list update:', ev);
              console.log('[USER_LIST] Current userList before update:', userList);
              setUserCount(ev.userCount);
              setUserList(ev.users || []);
              console.log('[USER_LIST] Set new userList to:', ev.users || []);
            } else if (ev.eventName === 'nickname_success') {
              console.log(`Nickname changed successfully: ${ev.oldNickname} -> ${ev.newNickname}`);
              // Update local nickname if needed
              setNickname(ev.newNickname);
            } else if (ev.eventName === 'nickname_error') {
              console.error(`Nickname error: ${ev.error} (requested: ${ev.requestedNickname})`);
              console.error(`Nickname "${ev.requestedNickname}" is already taken. Please choose another.`);
            } else if (ev.eventName === 'nickname_assigned') {
              console.log(`Server assigned nickname: ${ev.assignedNickname}`);
              setServerAssignedNickname(ev.assignedNickname);
              // Always use the server-assigned nickname initially
              setNickname(ev.assignedNickname);
              setIsVerifiedUser(false);
            } else if (ev.eventName === 'auth_success') {
              console.log(`Authentication successful: ${ev.user.handle}`);
              console.log('Setting isVerifiedUser to true');
              setNickname(ev.user.handle);
              setColor(ev.user.color);
              setIsVerifiedUser(true);
              // Update localStorage
              localStorage.setItem('nickname', ev.user.handle);
              localStorage.setItem('color', ev.user.color)
              
              // Add personal welcome system message
              const welcomeMessage = {
                id: Date.now(),
                text: `Welcome ${ev.assignedNickname}! To edit your nickname tap above or feel free to stay anonymous`,
                nickname: '[system]',
                color: '#999999',
                isSystemMessage: true,
                isPersonalMessage: true // Only visible to this user
              };
              
              setMessages(prevMessages => [...prevMessages, welcomeMessage]);
            }
          });
        }
      } catch (e) {
        console.log('Failed to parse message:', e);
      }
    };

    ws.onclose = (event) => {
      logger.warn('WEBSOCKET', 'WebSocket disconnected', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      scheduleReconnect();
    };

    ws.onerror = (err) => {
      console.error('WebSocket encountered error:', err);
      console.error('WebSocket readyState:', ws.readyState);
      console.error('WebSocket URL:', ws.url);
      console.error('Error details:', {
        type: err.type,
        target: err.target,
        isTrusted: err.isTrusted
      });
      
      // Log the error for debugging
      console.error('WebSocket connection failed');
      
      ws.close();
    };
  }, []); // No dependencies - only connect once on mount

  const scheduleReconnect = () => {
    // Don't schedule reconnection if we've already tried too many times
    if (reconnectAttempts.current >= 5) {
      console.warn('Max reconnection attempts reached, stopping reconnection attempts');
      return;
    }
    
    const delay = Math.min(10000, 1000 * 2 ** reconnectAttempts.current);
    reconnectAttempts.current++;
    console.log(`Scheduling reconnection attempt ${reconnectAttempts.current} in ${delay}ms`);
    reconnectTimeout.current = setTimeout(connectWebSocket, delay);
  };

  // Check for existing auth token on app load
  useEffect(() => {
    const checkAuthOnLoad = async () => {
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        try {
          const response = await fetch('/api/authenticate', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setIsVerifiedUser(true);
            setNickname(data.user.handle);
            setColor(data.user.color);
            console.log('Auth token valid, user verified:', data.user.handle);
          } else {
            // Invalid token, remove it
            localStorage.removeItem('authToken');
            localStorage.removeItem('nickname');
            localStorage.removeItem('color');
            setIsVerifiedUser(false);
            console.log('Auth token invalid, removed from storage');
          }
        } catch (error) {
          console.error('Error checking auth token:', error);
          setIsVerifiedUser(false);
        }
      }
    };
    
    checkAuthOnLoad();
  }, []); // Only run once on mount

  useEffect(() => {
    logger.info('REACT', 'WebSocket useEffect mounting - calling connectWebSocket');
    connectWebSocket();
    return () => {
      logger.info('REACT', 'WebSocket useEffect cleanup - closing connections');
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
    };
  }, []); // Only run once on mount

  // Auto-scroll to bottom when new messages arrive and user is at bottom, or when user sends a message
  useEffect(() => {
    if ((isAtBottom && messageCount > 0) || userJustSentMessage) {
      setTimeout(scrollToBottom, 0);
      if (userJustSentMessage) {
        setUserJustSentMessage(false);
      }
    }
  }, [messageCount, isAtBottom, scrollToBottom, userJustSentMessage]);

  // Add scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Reload messages when chaos mode changes
  useEffect(() => {
    const reloadMessages = async () => {
      if (!socketRef.current) return; // Don't reload if WebSocket isn't connected yet
      
      try {
        const isProduction = import.meta.env.MODE === 'production';
        const baseUrl = isProduction ? 'https://grotto.fm' : 'http://localhost:4000';
        const response = await fetch(`${baseUrl}/api/messages/initial?limit=50&chaosMode=${isChaosMode}`);
        
        if (response.ok) {
          const freshMessages = await response.json();
          setMessages(freshMessages);
          setMessageCount(freshMessages.length);
          
          // Reset pagination state
          if (freshMessages.length < 50) {
            setHasMoreMessages(false);
          } else {
            setHasMoreMessages(true);
          }
          
          // Scroll to bottom after reloading messages
          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
          }, 100);
        }
      } catch (error) {
        console.error('Error reloading messages after chaos mode change:', error);
      }
    };

    // Only reload if we have messages already (i.e., not on initial load)
    if (messages.length > 0) {
      reloadMessages();
    }
  }, [isChaosMode]); // Only when chaos mode changes

  const handleSend = () => {
    if (socketRef.current?.readyState === WebSocket.OPEN && message.trim()) {
      // Check for slash commands (only in chaos mode)
      if (message.startsWith('/') && isChaosMode) {
        handleSlashCommand(message.trim());
        setMessage('');
        return;
      }
      
      const messageData = JSON.stringify({
        eventName: 'message',
        text: message,
        nickname,
        color,
        id: Date.now(),
      });
      socketRef.current.send(messageData);
      setMessage('');
      // Mark that user just sent a message to trigger auto-scroll
      setUserJustSentMessage(true);
    }
  };

  // Define available slash commands
  const slashCommands = [
    {
      command: '/fireball',
      description: 'Launch a fireball',
      category: 'Effects'
    },
    {
      command: '/sparkles',
      description: 'Create magical sparkles',
      category: 'Effects'
    },
    {
      command: '/confetti',
      description: 'Burst confetti',
      category: 'Effects'
    },
    {
      command: '/lightning',
      description: 'Summon lightning',
      category: 'Effects'
    },
    {
      command: '/smoke',
      description: 'Blow smoke',
      category: 'Effects'
    },
    {
      command: '/lasers',
      description: 'Activate lasers',
      category: 'Effects'
    },
    {
      command: '/chicken',
      description: 'Summon a chicken',
      category: 'Effects'
    },
    {
      command: '/bird',
      description: 'Summon a cardinal',
      category: 'Effects'
    },
  ];

  // Define effect durations by type
  const getEffectDuration = (effectType) => {
    const durations = {
      fireball: 15000,    // 15 seconds
      sparkles: 10000,    // 10 seconds
      confetti: 8000,     // 8 seconds
      lightning: 5000,    // 5 seconds
      smoke: 20000,       // 20 seconds (12 active + 8 fade-out)
      lasers: 12000,      // 12 seconds
      chicken: 25000,     // 25 seconds (longer walking cycle)
      bird: 20000         // 20 seconds (flying around)
    };
    return durations[effectType] || 10000; // Default 10 seconds
  };

  const handleSlashCommand = (command) => {
    const cmd = command.toLowerCase();
    
    // Only process slash commands in chaos mode
    if (!isChaosMode) {
      return false;
    }
    
    
    if (cmd === '/fireball') {      
      const newEffect = {
        id: Date.now(),
        type: 'fireball',
        user: nickname,
        duration: getEffectDuration('fireball')
      };
      
      // Send effect through WebSocket and show locally immediately
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'effect',
          effect: newEffect
        }));
      } else {
        console.log('WebSocket not connected');
      }
      
      // Always add effect locally for immediate feedback
      setActiveEffects(prev => [...prev, newEffect]);
      
      // Add system message
      const systemMessage = {
        eventName: 'message',
        text: ` threw a fireball`,
        nickname: '[system]',
        color: '#ff6b35',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send system message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(systemMessage));
      }
      
      // Auto-remove effect after duration
      setTimeout(() => {
        setActiveEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
      }, newEffect.duration);
      
    } else if (cmd === '/sparkles') {
      
      const newEffect = {
        id: Date.now(),
        type: 'sparkles',
        user: nickname,
        duration: getEffectDuration('sparkles')
      };
      
      // Send and show effect
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'effect',
          effect: newEffect
        }));
      }
      
      setActiveEffects(prev => [...prev, newEffect]);
      
      const systemMessage = {
        eventName: 'message',
        text: ` created magical sparkles`,
        nickname: '[system]',
        color: '#ff6b35',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send system message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(systemMessage));
      }
      
      setTimeout(() => {
        setActiveEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
      }, newEffect.duration);
      
    } else if (cmd === '/confetti') {      
      const newEffect = {
        id: Date.now(),
        type: 'confetti',
        user: nickname,
        duration: getEffectDuration('confetti')
      };
      
      // Send and show effect
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'effect',
          effect: newEffect
        }));
      }
      
      setActiveEffects(prev => [...prev, newEffect]);
      
      const systemMessage = {
        eventName: 'message',
        text: ` bursted confetti everywhere`,
        nickname: '[system]',
        color: '#ff6b35',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send system message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(systemMessage));
      }
      
      setTimeout(() => {
        setActiveEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
      }, newEffect.duration);
      
    } else if (cmd === '/lightning') {
      
      const newEffect = {
        id: Date.now(),
        type: 'lightning',
        user: nickname,
        duration: getEffectDuration('lightning')
      };
      
      // Send and show effect
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'effect',
          effect: newEffect
        }));
      }
      
      setActiveEffects(prev => [...prev, newEffect]);
      
      const systemMessage = {
        eventName: 'message',
        text: ` summoned lightning`,
        nickname: '[system]',
        color: '#ff6b35',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send system message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(systemMessage));
      }
      
      setTimeout(() => {
        setActiveEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
      }, newEffect.duration);
      
    } else if (cmd === '/smoke') {      
      const newEffect = {
        id: Date.now(),
        type: 'smoke',
        user: nickname,
        duration: getEffectDuration('smoke')
      };
      
      // Send and show effect
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'effect',
          effect: newEffect
        }));
      }
      
      setActiveEffects(prev => [...prev, newEffect]);
      
      const systemMessage = {
        eventName: 'message',
        text: ` activated the smoke machine`,
        nickname: '[system]',
        color: '#ff6b35',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send system message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(systemMessage));
      }
      
      setTimeout(() => {
        setActiveEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
      }, newEffect.duration);
      
    } else if (cmd === '/lasers') {
      
      const newEffect = {
        id: Date.now(),
        type: 'lasers',
        user: nickname,
        duration: getEffectDuration('lasers')
      };
      
      // Send and show effect
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'effect',
          effect: newEffect
        }));
      }
      
      setActiveEffects(prev => [...prev, newEffect]);
      
      const systemMessage = {
        eventName: 'message',
        text: ` shot lasers`,
        nickname: '[system]',
        color: '#ff6b35',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send system message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(systemMessage));
      }
      
      setTimeout(() => {
        setActiveEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
      }, newEffect.duration);
      
    } else if (cmd === '/chicken') {
      
      const newEffect = {
        id: Date.now(),
        type: 'chicken',
        user: nickname,
        duration: getEffectDuration('chicken')
      };
      
      // Send and show effect
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'effect',
          effect: newEffect
        }));
      }
      
      setActiveEffects(prev => [...prev, newEffect]);
      
      const systemMessage = {
        eventName: 'message',
        text: ` released a chicken`,
        nickname: '[system]',
        color: '#ff6b35',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send system message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(systemMessage));
      }
      
      setTimeout(() => {
        setActiveEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
      }, newEffect.duration);
      
    } else if (cmd === '/bird') {      
      const newEffect = {
        id: Date.now(),
        type: 'bird',
        user: nickname,
        duration: getEffectDuration('bird')
      };
      
      // Send and show effect
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'effect',
          effect: newEffect
        }));
      }
      
      setActiveEffects(prev => [...prev, newEffect]);
      
      const systemMessage = {
        eventName: 'message',
        text: ` released a cardinal`,
        nickname: '[system]',
        color: '#ff6b35',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send system message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(systemMessage));
      }
      
      setTimeout(() => {
        setActiveEffects(prev => prev.filter(effect => effect.id !== newEffect.id));
      }, newEffect.duration);
      
    } else {
      // Unknown command
      const errorMessage = {
        eventName: 'message',
        text: `Unknown command: ${command}.`,
        nickname: '[error]',
        color: '#f44336',
        id: Date.now(),
        isSystemMessage: true
      };
      
      // Send error message to server so it gets stored in database
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(errorMessage));
      }
      
      setMessages(prev => [errorMessage, ...prev]);
    }
  };

  const handleNicknameSubmit = () => {
    const trimmedNickname = nickname.trim();
    
    if (!trimmedNickname) {
      setUsernameError('Username cannot be empty');
      return;
    }
    
    if (!validateUsername(trimmedNickname)) {
      setUsernameError('Username must be 2-20 characters: lowercase letters, numbers, underscores, and dashes only');
      return;
    }
    
    setUsernameError('');
    localStorage.setItem('nickname', trimmedNickname);
    localStorage.setItem('color', color);
    setIsNicknameSet(true);
    
    // Send join announcement after a brief delay to ensure WebSocket is ready
    setTimeout(() => {
      if (wsManager.getReadyState() === WebSocket.OPEN) {
        const joinMessageData = {
          eventName: 'message',
          text: `${trimmedNickname} has joined the chat`,
          nickname: '[system]',
          color: '#999999',
          id: Date.now(),
          isSystemMessage: true
        };

        const joinMessage = JSON.stringify(joinMessageData);
        socketRef.current.send(joinMessage);
      }
    }, 100);
  };

  // Show nickname setup overlay when nickname is not set
  if (!isNicknameSet) {
    return (
      <div className={`chat ${isCollapsed ? 'chat-collapsed' : ''}`}>      
        <div className={`messages ${isAtTop ? 'messages-at-top' : ''}`} ref={messagesContainerRef}>
          {messages.filter(({ nickname }) => {
            // Hide system messages in regular mode (identify by nickname)
            const isSystemMessage = nickname === '[system]' || nickname === '[error]';
            if (isSystemMessage && !isChaosMode) {
              return false; // Hide system messages in regular mode
            }
            return true;
          }).map(({ text, nickname, color, id, reactions = {} }) => {
            const isSystem = nickname === '[system]' || nickname === '[error]';
            return (
              <div key={id} className={`message ${isSystem ? 'system-message' : ''}`}>
                {isSystem ? (
                  <span className="system-text">
                    <strong style={{ color }}>{nickname}:</strong> {text}
                  </span>
                ) : (
                  <>
                    <strong style={{ color }}>{nickname}:</strong> {text}
                  </>
                )}
              
              
              {/* Display reactions only if someone has reacted */}
              {Object.keys(reactions).length > 0 && (
                <div className="reactions">
                  {Object.entries(reactions).map(([emoji, data]) => emoji !== 'undefined' && (
                    <div key={emoji} className="reaction">
                      {emoji} <span className="reaction-user-count">{data.users.length}</span>
                      <span className="tooltip">{data.users.join(', ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
        <div className="chatinput">
          <div className="nickname-setup-inline">
            <input
              className="nickname-input"
              type="text"
              placeholder="Enter your nickname"
              value={nickname}
              onChange={(e) => {
                const sanitized = sanitizeUsername(e.target.value);
                setNickname(sanitized);
                if (usernameError && sanitized !== e.target.value) {
                  setUsernameError('');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleNicknameSubmit();
                }
              }}
            />
            <input
              className="color-input"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
            <button onClick={handleNicknameSubmit}>Join Chat</button>
          </div>
          {usernameError && (
            <div className="username-error">
              {usernameError}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`chat ${isCollapsed ? 'chat-collapsed' : ''}`}>
      {/* Chat Header */}
      <div className="chat-header">
        <span className="chat-title">Chat</span>
        <button className="edit-profile-button" onClick={() => {
          if (!isEditing) {
            // Reset all auth states when opening modal
            setIsClaimingNickname(false);
            setIsLoggingIn(false);
          }
          setIsEditing(!isEditing);
        }}>
          {(() => {
            console.log('Button render - isEditing:', isEditing, 'isVerifiedUser:', isVerifiedUser);
            return isEditing ? 'Cancel' : isVerifiedUser ? 'Edit Profile' : 'Claim Nickname';
          })()}
        </button>
      </div>
      
      <div className={`messages ${isAtTop ? 'messages-at-top' : ''}`} ref={messagesContainerRef}>
        {/* Loading indicator */}
        {isLoadingOlderMessages && (
          <div className="loading-messages">
            Loading older messages...
          </div>
        )}
        
        {/* Messages List - Natural Height */}
        {messages.filter((message) => {
          const { nickname } = message;

          // Hide system messages in regular mode (identify by nickname)
          const isSystemMessage = nickname === '[system]' || nickname === '[error]';
          if (isSystemMessage && !isChaosMode) {
            return false; // Hide system messages in regular mode
          }
          return true;
        }).map((message, index, filteredMessages) => {
          const { text, nickname, color, id, reactions = {} } = message;
          const isSystem = nickname === '[system]' || nickname === '[error]';
          
          // Check if this message is from the same user as the next message (due to column-reverse)
          const nextMessage = filteredMessages[index + 1];
          
          // Check if nextMessage is system message  
          const nextIsSystem = nextMessage && (nextMessage.nickname === '[system]' || nextMessage.nickname === '[error]');
          
          const isGrouped = nextMessage && 
            nextMessage.nickname === nickname && 
            !isSystem && 
            !nextIsSystem;
          
          return (
            <div key={id} className={`message ${isSystem ? 'system-message' : ''} ${isGrouped ? 'grouped-message' : ''}`}>
              {isSystem && isChaosMode ? (
                <span className="system-text">
                  <strong style={{ color }}>{nickname}:</strong> {text}
                </span>
              ) : !isSystem ? (
                <>
                  {!isGrouped && <strong style={{ color }}>{nickname}:</strong>} {text}
                </>
              ) : null}
            
              {/* Display reactions only if someone has reacted */}
              {Object.keys(reactions).length > 0 && (
                <div className="reactions">
                {Object.entries(reactions).map(([emoji, data]) => emoji !== 'undefined' && (
                  <div 
                    key={emoji} 
                    className="reaction" 
                    onClick={() => handleReact(emoji, id)}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.display = 'block';
                        setTimeout(() => handleTooltipPosition(e, tooltip), 0);
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) tooltip.style.display = 'none';
                    }}
                  >
                    {emoji} <span className="reaction-user-count">{data.users.length}</span>
                    <span className="tooltip">{data.users.join(', ')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Quick reactions and generic react button - shown only on hover */}
            <div className="reaction-buttons">
              {['👍', '❤️', '😂', '🔥', '😮'].map((emoji) => (
                <button key={emoji} className="reaction-btn" onClick={() => handleReact(emoji, id)}>
                  {emoji}
                </button>
              ))}
              <button onClick={() => toggleEmojiPicker(id)}>➕</button>
            </div>
          </div>
          );
        })}
        
        {/* Load More Button as the last message - only show if there are more messages */}
        {hasMoreMessages && (
          <div className="message load-more-message">
            <button 
              className="load-more-button"
              onClick={loadOlderMessages}
              disabled={isLoadingOlderMessages}
            >
              {isLoadingOlderMessages ? 'Loading...' : 'Load More Messages'}
            </button>
          </div>
        )}
      </div>
      
      {/* Slash Command Menu */}
      {showSlashMenu && (
        <div className="slash-menu">
          <div className="slash-menu-header">
            <span>Available Commands</span>
          </div>
{slashCommands
            .filter(cmd => 
              message === '/' || cmd.command.toLowerCase().startsWith(message.toLowerCase())
            )
            .map((cmd, index) => (
              <div 
                key={index} 
                className={`slash-menu-item ${index === selectedCommandIndex ? 'selected' : ''}`}
                onClick={() => {
                  setMessage(cmd.command + ' ');
                  setShowSlashMenu(false);
                  setSelectedCommandIndex(0);
                  textareaRef.current?.focus();
                }}
              >
                <div className="slash-command">{cmd.command}</div>
                <div className="slash-category">{cmd.category}</div>
              </div>
            ))}
        </div>
      )}

      {/* Chat Input - Fixed at bottom */}
      <div className="chatinput">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => {
            const value = e.target.value;
            setMessage(value);
            
            // Show slash command menu when typing '/' at start of message (only in chaos mode)
            if (isChaosMode && (value === '/' || (value.startsWith('/') && !value.includes(' ')))) {
              setShowSlashMenu(true);
              setSelectedCommandIndex(0);
            } else {
              setShowSlashMenu(false);
              setSelectedCommandIndex(0);
            }
          }}
          placeholder="Type your message..."
          onKeyDown={(e) => {
            if (showSlashMenu) {
              const filteredCommands = slashCommands.filter(cmd => 
                message === '/' || cmd.command.toLowerCase().startsWith(message.toLowerCase())
              );
              
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedCommandIndex(prev => 
                  prev < filteredCommands.length - 1 ? prev + 1 : 0
                );
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedCommandIndex(prev => 
                  prev > 0 ? prev - 1 : filteredCommands.length - 1
                );
              } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (filteredCommands[selectedCommandIndex]) {
                  setMessage(filteredCommands[selectedCommandIndex].command + ' ');
                  setShowSlashMenu(false);
                  setSelectedCommandIndex(0);
                }
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setShowSlashMenu(false);
                setSelectedCommandIndex(0);
              }
            } else if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <div className="chatinput-actions">
          <button data-emoji-button="input" onClick={() => toggleEmojiPicker(null)}>😊</button>
          <button className="circular-button" onClick={handleSend}>↑</button>
        </div>
      </div>
      
      {/* Edit Profile Modal */}
      {isEditing && (
        <div className="edit-profile">
          {isVerifiedUser ? (
            // Profile editing for verified users - show status only
            <>
              <div className="profile-info">
                <span>Nickname: <strong>{nickname}</strong> (verified)</span>
              </div>
            </>
          ) : (
            // Login/Registration options for new users
            <div className="auth-options">
              {!isClaimingNickname && !isLoggingIn ? (
                // Show both options
                <div className="auth-buttons">
                  <button onClick={() => setIsClaimingNickname(true)}>Claim Nickname</button>
                  <button onClick={() => setIsLoggingIn(true)}>Login</button>
                </div>
              ) : isClaimingNickname ? (
                // Claim nickname flow
                <div className="claim-nickname-flow">
                  <input
                    className="email-input"
                    type="email"
                    placeholder="Enter your email"
                    value={claimEmail}
                    onChange={(e) => setClaimEmail(e.target.value)}
                  />
                  <input
                    className="nickname-input"
                    type="text"
                    placeholder="Desired nickname"
                    value={claimNickname}
                    onChange={(e) => {
                      const sanitized = sanitizeUsername(e.target.value);
                      setClaimNickname(sanitized);
                    }}
                  />
                  <input
                    className="color-input"
                    type="color"
                    value={claimColor}
                    onChange={(e) => setClaimColor(e.target.value)}
                  />
                  <div className="button-row">
                    <button onClick={handleClaimNickname}>Send Verification Code</button>
                    <button onClick={() => setIsClaimingNickname(false)}>Back</button>
                  </div>
                </div>
              ) : (
                // Login flow
                <div className="login-flow">
                  <input
                    className="email-input"
                    type="email"
                    placeholder="Enter your email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                  />
                  <div className="button-row">
                    <button onClick={handleLogin}>Send Login Code</button>
                    <button onClick={() => setIsLoggingIn(false)}>Back</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Verification Code Popup */}
      {showVerificationForm && (
        <div className="verification-popup-backdrop">
          <div className="verification-popup">
            <h3>Enter Verification Code</h3>
            <p>We sent a 6-digit code to <strong>{pendingVerification?.email}</strong></p>
            <input
              className="verification-code-input"
              type="text"
              placeholder="Enter 6-digit code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              maxLength={6}
            />
            <div className="verification-actions">
              <button className="verify-button" onClick={handleVerifyCode}>
                Verify & Claim
              </button>
              <button className="cancel-button" onClick={() => {
                setShowVerificationForm(false);
                setPendingVerification(null);
                setVerificationCode('');
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reaction emoji picker */}
      {emojiVisibleMessageId && (
        <>
          <div 
            className="emoji-picker-backdrop"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 99999
            }}
          />
          <div 
            data-emoji-picker="reaction"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 100000,
              background: 'rgba(0, 0, 0, 0.9)',
              borderRadius: '8px',
              padding: '10px',
              backdropFilter: 'blur(5px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)'
            }}>
            <Picker 
              data={data} 
              onEmojiSelect={(emoji) => {
                handleReact(emoji.native, emojiVisibleMessageId);
                setEmojiVisibleMessageId(null);
              }} 
            />
          </div>
        </>
      )}
      {isEmojiPickerVisible && (
          <>
            <div 
              className="emoji-picker-backdrop"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                zIndex: 9999
              }}
            />
            <div 
              data-emoji-picker="input"
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10000,
                background: 'rgba(0, 0, 0, 0.9)',
                borderRadius: '8px',
                padding: '10px',
                backdropFilter: 'blur(5px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)'
              }}>
              <Picker data={data} onEmojiSelect={handleEmojiSelect} />
            </div>
          </>
        )}

      {/* Mobile auth button - show for all users */}
      <button className="mobile-auth-button" onClick={() => {
        if (!isEditing) {
          // Reset all auth states when opening modal
          setIsClaimingNickname(false);
          setIsLoggingIn(false);
        }
        setIsEditing(!isEditing);
      }}>
        {isEditing ? 'Cancel' : isVerifiedUser ? 'Edit Profile' : 'Claim Nickname'}
      </button>
    </div>
  );
}

const VideoPlayer = ({ streamUrl, isStreaming, onPlayingStateChange, onRefsReady }) => {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const DEBUG_MODE = false; // Set to true to show test image
  const AUDIO_TEST_MODE = false; // Set to true to enable audio testing
  const [testAudioFile, setTestAudioFile] = useState(AUDIO_TEST_MODE ? '/src/assets/Begging.mp3' : null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!isStreaming && !testAudioFile) {
      setIsPlaying(false);
      return;
    }

    const video = videoRef.current;
    const audio = audioRef.current;
    
    // Add event listeners to track playing state
    const handlePlay = () => {
      setIsPlaying(true);
      onPlayingStateChange?.(true);
    };
    const handlePause = () => {
      setIsPlaying(false);
      onPlayingStateChange?.(false);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      onPlayingStateChange?.(false);
    };

    // Add listeners to both video and audio elements
    if (video) {
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);
    }
    
    if (audio) {
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
    }

    // Check if HLS.js is supported
    if (Hls.isSupported()) {
      const hls = new Hls({
        // Low-latency HLS configuration
        liveSyncDurationCount: 1,     // Only keep 1 segment ahead
        liveMaxLatencyDurationCount: 2, // Max 2 segments latency
        liveDurationInfinity: true,   // Don't limit live playlist
        highBufferWatchdogPeriod: 1,  // Check buffer every 1 second
        nudgeOffset: 0.1,             // Small nudge for sync
        nudgeMaxRetry: 5,             // Retry sync up to 5 times
        maxBufferLength: 2,           // Keep max 2 seconds in buffer
        maxMaxBufferLength: 4,        // Absolute max 4 seconds
        maxBufferSize: 2 * 1000 * 1000, // 2MB max buffer
        maxBufferHole: 0.1,           // Max 100ms buffer holes
      });
      hls.loadSource(streamUrl);  // Load the stream URL
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, function () {
        video.play().catch(() => setHasUserInteracted(false));
      });

      return () => {
        if (video) {
          video.removeEventListener('play', handlePlay);
          video.removeEventListener('pause', handlePause);
          video.removeEventListener('ended', handleEnded);
        }
        if (audio) {
          audio.removeEventListener('play', handlePlay);
          audio.removeEventListener('pause', handlePause);
          audio.removeEventListener('ended', handleEnded);
        }
        hls.destroy();  // Clean up the HLS.js instance on component unmount
      };
    } else if (video && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Fallback for Safari (which has native HLS support)
      video.src = streamUrl;
      video.addEventListener('canplay', () => video.play().catch(() => setHasUserInteracted(false)));
    }

    return () => {
      if (video) {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
      }
      if (audio) {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
      }
    };
  }, [streamUrl, isStreaming, testAudioFile]);

  // Pass refs back to parent
  useEffect(() => {
    onRefsReady?.(videoRef, audioRef);
    return () => {
      onRefsReady?.({ current: null }, { current: null });
    };
  }, [onRefsReady]);

  return (
    <div className="video-container">
      {/* Audio Test Mode */}
      {AUDIO_TEST_MODE && !isStreaming && (
        <div className="audio-test-panel" style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          background: 'rgba(0, 0, 0, 0.8)',
          padding: '10px',
          borderRadius: '8px',
          zIndex: 10
        }}>
          <label style={{ color: 'white', display: 'block', marginBottom: '10px' }}>
            🎵 Audio Visualizer Test - Begging.mp3
          </label>
          <audio 
            ref={audioRef} 
            src={testAudioFile} 
            controls 
            style={{ width: '100%' }}
            onPlay={() => {
              console.log('Audio started playing - user interaction detected');
            }}
          />
        </div>
      )}
      
      {!isStreaming && !testAudioFile ? (
        <div className="offline">
          {DEBUG_MODE ? (
            <img 
              src="https://picsum.photos/360/640?random=1" 
              alt="Test background" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover'
              }}
            />
          ) : (
            <div className="offline-overlay">
              <div className="offline-icon">🔴</div>
              <div className="offline-text">OFFLINE</div>
              <div className="offline-subtitle">Stream will appear here when live</div>
            </div>
          )}
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            width="100%"
            height="100%"
            style={{ objectFit: 'cover' }}
          >
            <p>Your browser does not support HTML5 video.</p>
          </video>
          {!hasUserInteracted && !AUDIO_TEST_MODE && (
            <button 
              className="playbutton" 
              onClick={() => {
                videoRef.current.play();
                setHasUserInteracted(true);
              }}
            >
              ▶
            </button>
          )}
        </>
      )}
    </div>
  );
};

function useStreamingStatus() {
  const [isStreaming, setIsStreaming] = useState(false);

  async function checkStreamingStatus() {
    const response = await fetch('https://grotto.fm/stat');
    const text = await response.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, "application/xml");

    // Check for active streams
    const streams = xmlDoc.getElementsByTagName("stream");
    setIsStreaming(streams.length > 0); // Set state based on streaming status
  }

  useEffect(() => {
    checkStreamingStatus();
  }, []);

  return [isStreaming, checkStreamingStatus];
}

function App() {
  // Component lifecycle logging
  logger.info('REACT', 'App component function called (render/re-render)');
  
  // Small change to trigger HMR
  const [isStreaming, checkStreamingStatus] = useStreamingStatus();
  const streamUrl = 'https://grotto.fm/hls/grotto.m3u8';
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [activeEffects, setActiveEffects] = useState([]);
  const audioDataRef = useRef(null);
  
  // Chaos Mode toggle - controls WebGL effects, commands, and system messages
  const [isChaosMode, setIsChaosMode] = useState(() => {
    const saved = localStorage.getItem('chaosMode');
    return saved !== null ? JSON.parse(saved) : true; // Default to true (enabled)
  });

  // User list state
  const [userList, setUserList] = useState([]);
  const [userCount, setUserCount] = useState(0);

  const savedMediaMode = typeof window !== 'undefined'
    ? window.localStorage.getItem('grottoPreferredMediaMode')
    : null;
  const hasStoredMediaPreference = savedMediaMode === 'live' || savedMediaMode === 'archive';
  const [mediaMode, setMediaMode] = useState(() => {
    if (hasStoredMediaPreference) {
      return savedMediaMode;
    }
    return 'live';
  });
  const userHasSetMediaMode = useRef(hasStoredMediaPreference);
  
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsChaosMode(false); // Disable chaos mode on mobile
        localStorage.setItem('chaosMode', JSON.stringify(false));
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set the background image CSS custom property
  useEffect(() => {
    document.documentElement.style.setProperty('--grotto-bg-image', `url(${grottoWebp})`);
  }, []);

  
  // Refs for audio visualizer
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  
  // Audio data callback that updates ref for real-time access
  const handleAudioData = useCallback((data) => {
    audioDataRef.current = data;
  }, []);
  const archivePlaylistId = import.meta.env.VITE_ARCHIVE_PLAYLIST_ID || DEFAULT_ARCHIVE_PLAYLIST_ID;

  // Callback to receive refs from VideoPlayer
  const handleRefsReady = useCallback((vRef, aRef) => {
    videoRef.current = vRef?.current ?? null;
    audioRef.current = aRef?.current ?? null;
  }, []);

  const [isPlaying, setIsPlaying] = useState(false);
  const handleMediaModeChange = useCallback((nextMode) => {
    userHasSetMediaMode.current = true;
    setMediaMode(nextMode);
  }, []);

  useEffect(() => {
    if (mediaMode !== 'live') {
      setIsPlaying(false);
      videoRef.current?.pause?.();
      audioRef.current?.pause?.();
      videoRef.current = null;
      audioRef.current = null;
    }

    if (typeof window !== 'undefined' && userHasSetMediaMode.current) {
      window.localStorage.setItem('grottoPreferredMediaMode', mediaMode);
    }
  }, [mediaMode]);

  useEffect(() => {
    if (userHasSetMediaMode.current) {
      return;
    }
    setMediaMode((currentMode) => {
      const desiredMode = isStreaming ? 'live' : 'archive';
      return currentMode === desiredMode ? currentMode : desiredMode;
    });
  }, [isStreaming]);
  
  // Development helper: expose reset function globally
  useEffect(() => {
    if (import.meta.env.MODE === 'development') {
      window.resetUser = () => {
        localStorage.removeItem('nickname');
        localStorage.removeItem('color');
        window.location.reload();
      };
    }
  }, []);
  
  return (
    <>
      {/* WebGL Shader Background - only show in chaos mode */}
      {isChaosMode && <WebGLBackground zIndex={-10} audioDataRef={audioDataRef} />}
      
      {/* WebGL Audio Visualizer Background - only show in chaos mode */}
  {isChaosMode && <AudioVisualizer videoRef={videoRef} audioRef={audioRef} isPlaying={isPlaying && mediaMode === 'live'} activeEffects={activeEffects} onAudioData={handleAudioData} />}
      
      {/* WebGL Canvas Overlay - only show in chaos mode */}
      {isChaosMode && <WebGLCanvas 
        zIndex={999} 
        activeEffects={activeEffects}
        effectClasses={{
          sparkles: WebGLSparkles,
          confetti: WebGLConfetti,
          fireball: WebGLFireball,
          lightning: WebGLLightning,
          smoke: WebGLSmoke,
          lasers: WebGLLasers,
          chicken: WebGLChicken,
          bird: WebGLBird
        }}
      />}
      
      <Marquee />
      <div className="main">
        <img className="logo" src={grotto} alt="grotto" />
        
        {/* Chaos Mode Toggle - show on all devices */}
        <div className="chaos-mode-toggle">
          <label className="toggle-switch">
            <input 
              type="checkbox" 
              checked={isChaosMode}
              onChange={(e) => {
                const newValue = e.target.checked;
                setIsChaosMode(newValue);
                localStorage.setItem('chaosMode', JSON.stringify(newValue));
              }}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">Chaos Mode</span>
          </label>
        </div>
        
        <div className="media-panel">
          <div className="media-player-wrapper">
            <div className="media-tabs" role="tablist" aria-label="Grotto media selection">
              <button
                type="button"
                className={`media-tab ${mediaMode === 'live' ? 'active' : ''}`}
                onClick={() => handleMediaModeChange('live')}
                aria-selected={mediaMode === 'live'}
                role="tab"
              >
                Live
              </button>
              <button
                type="button"
                className={`media-tab ${mediaMode === 'archive' ? 'active' : ''}`}
                onClick={() => handleMediaModeChange('archive')}
                aria-selected={mediaMode === 'archive'}
                role="tab"
              >
                Archive
              </button>
            </div>
            {mediaMode === 'live' ? (
              <VideoPlayer 
                streamUrl={streamUrl} 
                isStreaming={isStreaming} 
                onPlayingStateChange={setIsPlaying}
                onRefsReady={handleRefsReady}
              />
            ) : (
              <div className="video-container archive-container">
                {archivePlaylistId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/videoseries?list=${archivePlaylistId}&rel=0&modestbranding=1`}
                    title="Grotto.fm archive playlist"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <div className="archive-placeholder">
                    <div className="archive-placeholder-title">Archive playlist not configured</div>
                    <p>Set the <code>VITE_ARCHIVE_PLAYLIST_ID</code> environment variable to display archived videos.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="chat-container">
          <div className={`shoutbox ${isChatCollapsed ? 'chat-collapsed' : ''}`}>
            <Chat 
              checkStreamingStatus={checkStreamingStatus} 
              isCollapsed={isChatCollapsed}
              activeEffects={activeEffects}
              setActiveEffects={setActiveEffects}
              isChaosMode={isChaosMode}
              userCount={userCount}
              setUserCount={setUserCount}
              userList={userList}
              setUserList={setUserList}
            />
          </div>
          {!isChatCollapsed && (
            <div className="user-list">
              <div className="user-list-header">
                <span className="user-count">{userCount} online</span>
              </div>
              <div className="user-list-content">
                {userList.map((user, index) => (
                  <div 
                    key={`${user.nickname}-${index}`} 
                    className={`user-item${user.verified ? ' user-verified' : ''}`}
                    style={{ 
                      color: user.color
                    }}
                  >
                    <span className="user-nickname">{user.nickname}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {!isChatCollapsed && (
          <button className="collapse-caret" onClick={() => setIsChatCollapsed(true)}></button>
        )}
        {isChatCollapsed && (
          <button className="fab-button" onClick={() => setIsChatCollapsed(false)}>
            💬
          </button>
        )}
      </div>
    </>
  );
}

export default App;
